/**
 * Seo component — uses React 19's native metadata hoisting.
 *
 * React 19 automatically moves <title>, <meta>, and <link> tags to <head>
 * regardless of where they appear in the component tree, with sensible
 * deduplication. We render them inline; React puts them in the right place.
 *
 * <script type="application/ld+json"> is rendered in the body — Google's
 * structured data crawler scans the entire DOM, so this works fine for SEO.
 */

const SITE_NAME = "PartyCostumeShop";
const SITE_TAGLINE = "Costumes, curated. Parties, delivered.";
const DEFAULT_DESCRIPTION =
  "Thousands of hand-picked costumes & party supplies from Amazon and eBay — assembled by people who actually like Halloween. No carts, no checkouts. Just pick, click, done.";

const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?auto=format&fit=crop&w=1200&q=80";

const META_DESCRIPTION_MAX = 160;

/**
 * Strip Markdown syntax + collapse whitespace so a description authored as
 * Markdown still reads cleanly in <meta description>, OG, and Twitter cards.
 * (Search engines and social-card previews render the text verbatim.)
 */
function plainDescription(text) {
  if (!text) return "";
  let s = String(text)
    // fenced code blocks
    .replace(/```[\s\S]*?```/g, " ")
    // inline code
    .replace(/`([^`]+)`/g, "$1")
    // images ![alt](url)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    // links [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // headings, blockquotes, list markers at line start
    .replace(/^[ \t]*(?:#{1,6}|>|[-*+]|\d+\.)\s+/gm, "")
    // bold / italic / strike — keep the inner text
    .replace(/(\*\*|__|\*|_|~~)(.+?)\1/g, "$2")
    // horizontal rules
    .replace(/^\s*[-*_]{3,}\s*$/gm, " ")
    // collapse runs of whitespace
    .replace(/\s+/g, " ")
    .trim();
  if (s.length > META_DESCRIPTION_MAX) {
    s = s.slice(0, META_DESCRIPTION_MAX - 1).replace(/\s+\S*$/, "") + "…";
  }
  return s;
}

function origin() {
  if (typeof window === "undefined") return "";
  return window.location.origin.replace(/\/+$/, "");
}

function abs(img) {
  if (!img) return null;
  if (img.startsWith("//")) return `https:${img}`;
  if (img.startsWith("/")) return `${origin()}${img}`;
  return img;
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  path = "",
  type = "website",
  noindex = false,
  schema = null,
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} — ${SITE_TAGLINE}`;
  const cleanDescription = plainDescription(description) || DEFAULT_DESCRIPTION;
  const url = `${origin()}${path}`;
  const ogImage = abs(image);
  const robots = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large";

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={cleanDescription} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanDescription} />
      <meta property="og:url" content={url} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      <meta name="theme-color" content="#FFFDF7" />

      {/* JSON-LD — Google's structured-data crawler reads from anywhere in the DOM */}
      {schema && (
        <script
          type="application/ld+json"
          // Escape "<" so a hostile product title like "</script><script>..." can't
          // break out of the JSON-LD context. This is the standard hardening for
          // <script>-embedded JSON.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema).replace(/</g, "\\u003c"),
          }}
        />
      )}
    </>
  );
}

// Schema builders
export function organizationSchema() {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: base || "https://partycostumeshop.com",
    logo: `${base}/apple-touch-icon.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [],
  };
}

export function websiteSchema() {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: base || "https://partycostumeshop.com",
    potentialAction: {
      "@type": "SearchAction",
      target: `${base}/search?q={query}`,
      "query-input": "required name=query",
    },
  };
}

export function productSchema(product) {
  const base = origin();
  const vendorName =
    product.vendor === "amazon"
      ? "Amazon"
      : product.vendor === "ebay"
        ? "eBay"
        : product.vendor;

  // priceValidUntil = ~30 days from now. Google Search Console warns when this
  // is missing or in the past — for affiliate listings we don't know the
  // actual end-date so we use a rolling 30d window and refresh on each render.
  const valid = new Date();
  valid.setDate(valid.getDate() + 30);
  const priceValidUntil = valid.toISOString().split("T")[0];

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.description || product.title,
    image: [product.image_url].filter(Boolean),
    sku: product.id,
    url: `${base}/product/${product.slug}`,
    brand: { "@type": "Brand", name: vendorName },
    // category_slugs is an array on the model — Google accepts a string,
    // so we join with " > " (their breadcrumb convention).
    ...(product.category_slugs && product.category_slugs.length > 0
      ? { category: product.category_slugs.join(" > ") }
      : {}),
    offers: {
      "@type": "Offer",
      url: `${base}/api/go/${product.slug}`,
      priceCurrency: product.currency || "USD",
      price: (product.price || 0).toFixed(2),
      priceValidUntil,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: vendorName },
    },
  };
}

export function articleSchema(article) {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt || article.title,
    image: [article.cover_image].filter(Boolean),
    datePublished: article.publish_at || article.created_at,
    // Use updated_at when present so Google notices edits.
    dateModified: article.updated_at || article.publish_at || article.created_at,
    author: { "@type": "Organization", name: SITE_NAME, url: base || undefined },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: base || undefined,
      logo: {
        "@type": "ImageObject",
        url: `${base}/apple-touch-icon.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${base}/article/${article.slug}`,
    },
  };
}

export function categorySchema(category, productCount) {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} Costumes & Supplies`,
    description: category.description,
    url: `${base}/category/${category.slug}`,
    image: category.image_url ? [category.image_url] : undefined,
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: base },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: productCount || 0,
    },
  };
}

/**
 * itemListSchema — emits a Schema.org ItemList with each product's full
 * Product schema inline. Used on category pages (and the homepage's
 * featured grid) to give Google a rich, structured view of what we sell.
 *
 *   list: { name: "Halloween costumes", url: "/category/halloween" }
 *   products: array of product objects
 */
export function itemListSchema(list, products) {
  if (!products || products.length === 0) return null;
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: list.name,
    url: `${base}${list.url || ""}`,
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${base}/product/${p.slug}`,
      name: p.title,
    })),
  };
}

export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}
